/*
  Subtest results for distinct agent/target pair
*/
import { action, observable, computed } from 'mobx';
import { getTrafficFlowQuery } from './utils';

class AgentTargetTestResultsState {
  get defaults() {
    return {
      // state data
      agent: null,
      target: null,
      test: null,
      trafficFlowQuery: {},
      // results indicators
      hasResults: false,
      hasTraceResults: false,
      // returned results
      aggregateResults: null,
      results: null,
      traceResults: null
    };
  }

  // TestModel
  test = this.defaults.test;

  // AgentModel
  agent = this.defaults.agent;

  @observable
  target = this.defaults.target;

  @observable
  hasResults = this.defaults.hasResults;

  @observable
  hasTraceResults = this.defaults.hasTraceResults;

  // subtest results for an agent/target pair
  @observable.ref
  results = this.defaults.results;

  // subtest trace results for an agent/target pair
  @observable.ref
  traceResults = this.defaults.traceResults;

  // the avg aggregate results returned from a syngest results request where aggregate=true
  @observable.ref
  aggregateResults = this.defaults.aggregateResults;

  // query that is ultimately run in the subtest results time series tab
  @observable.ref
  trafficFlowQuery = this.defaults.trafficFlowQuery;

  constructor({ store, test, agent, target }) {
    this.store = store;
    this.test = test;
    this.agent = agent;
    this.target = target;
  }

  @action
  setTestResults(testResults, results, aggregateResults) {
    const { test, agent, target } = this;
    const { actualStartDate: startDate, actualEndDate: endDate } = test.agentResults;
    const { flowFiltersByIp } = testResults;
    const hasResults = this.checkForTestResults(results);
    // mesh tests don't need flow filters provided, they are built in the getTrafficFlowQuery util
    const allowFlowQuery = !!flowFiltersByIp?.[target] || test.isMesh;

    if (hasResults) {
      this.results = results;
      let targetAgentId;
      // the only time we have a target agent id is when we are running a mesh test or agent to agent test
      // which is what the getTrafficFlowQuery util is looking for, or itll skip it
      if (test.isMesh || test.isAgentTest) {
        targetAgentId = target;
      }
      this.trafficFlowQuery = allowFlowQuery
        ? getTrafficFlowQuery(test, {
            $syn: this.store.$syn,
            startDate,
            endDate,
            flowFilters: flowFiltersByIp?.[target],
            agent_id: agent?.id,
            target: targetAgentId
          })
        : null;
    }

    this.hasResults = hasResults;
    this.aggregateResults = aggregateResults;
  }

  @action
  setTraceResults(traceResults, results) {
    const hasTraceResults = this.checkForTraceResults(results);

    if (hasTraceResults) {
      this.traceResults = results;
    }

    this.hasTraceResults = hasTraceResults;
  }

  // ----- computed properties -------------------------------------

  // determines whether a flow-based query is displayed in the time series tab
  @computed
  get displayFlowQuery() {
    if (this.test.isMesh || this.test.isAgentTest) {
      const targetAgentType = this.store.$syn.agents.get(this.target)?.get('agent_type');
      return !(this.agent.get('agent_type') === 'global' && targetAgentType === 'global');
    }
    return true;
  }

  @computed
  get resultTime() {
    const { test, hasResults } = this;
    const { resultTimeMs } = test.agentResults;

    if (hasResults && !!resultTimeMs) {
      return resultTimeMs / 1000;
    }

    return null;
  }

  // ping avg/min/max
  @computed
  get pingHealthAggregates() {
    return this.aggregateResults?.ping || {};
  }

  // dns avg/min/max
  @computed
  get dnsHealthAggregates() {
    return this.aggregateResults?.dns || {};
  }

  // http avg/min/max
  @computed
  get httpHealthAggregates() {
    return this.aggregateResults?.http || {};
  }

  // throughput avg/min/max
  @computed
  get throughputHealthAggregates() {
    return this.aggregateResults?.throughput || {};
  }

  // ping or knock
  @computed
  get pingHealthTs() {
    const { results, hasResults } = this;

    if (hasResults && !!results.ping) {
      return Object.values(results.ping).map((health) => ({
        ...health,
        ...health?.overall_health, // adds the 'health' property
        time: +health.time * 1000
      }));
    }

    return null;
  }

  // throughput
  @computed
  get throughputHealthTs() {
    const { results, hasResults } = this;
    if (hasResults && !!results?.throughput) {
      return Object.values(results?.throughput).map((health) => ({
        ...health,
        ...health?.overall_health, // adds the 'health' property
        time: +health.time * 1000
      }));
    }

    return null;
  }

  // dns resolution
  get dnsHealthTs() {
    const { results, hasResults } = this;
    if (hasResults && !!results.dns) {
      return Object.values(results.dns).map((health) => ({
        ...health,
        ...health?.overall_health, // adds the 'health' property
        time: +health.time * 1000
      }));
    }

    return null;
  }

  // ping or knock
  @computed
  get pingHealthTsResult() {
    const { results, hasResults, resultTime } = this;

    if (hasResults && !!results.ping?.[resultTime]) {
      return results.ping[resultTime];
    }

    return null;
  }

  // dns resolution
  @computed
  get dnsHealthTsResult() {
    const { results, hasResults, resultTime } = this;

    if (hasResults && !!results.dns?.[resultTime]) {
      return results.dns[resultTime];
    }

    return null;
  }

  // http or page load
  @computed
  get httpHealthTs() {
    const { results, hasResults } = this;

    if (hasResults && !!results.http) {
      return Object.values(results.http).map((health) => ({
        ...health,
        ...health?.overall_health, // adds the 'health' property
        time: +health.time * 1000
      }));
    }

    return null;
  }

  // http or page load
  @computed
  get httpHealthTsResult() {
    const { results, hasResults, resultTime } = this;

    if (hasResults && !!results.http?.[resultTime]) {
      return results.http[resultTime];
    }

    return null;
  }

  // page load or transaction
  @computed
  get harTsResult() {
    const { results, hasResults, resultTime } = this;

    if (hasResults && !!results.http?.[resultTime]?.har) {
      return results.http[resultTime].har;
    }

    return [];
  }

  // transaction
  @computed
  get screenshotsTsResult() {
    const { results, hasResults, resultTime } = this;

    if (hasResults && !!results.http?.[resultTime]?.screenshots) {
      return results.http[resultTime].screenshots;
    }

    return [];
  }

  @computed
  get traceroutes() {
    const { traceResults, hasTraceResults } = this;
    if (hasTraceResults) {
      return Object.entries(traceResults).map(([ts, health]) => {
        const { count, distance, hopCount, probes, target_ip } = health;
        return {
          count,
          distance,
          hopCount,
          time: +ts * 1000,
          // we want to display the average value of a single probe in the 'path hops' graph
          // we'll already have the correct value in count.[average|distance] here, however parent test results will not
          // we define these 'actual' properties here as duplicates so we can consistently refer to data in the 'path hops' graph
          actualHopCount: count.average,
          actualDistance: distance.average,
          traces: [
            {
              agent_ip: '::',
              agentId: this.agent.id,
              hopCount,
              probes,
              target_ip
            }
          ]
        };
      });
    }
    return [];
  }

  // ----- class utils ------------------------------------------------

  checkForTestResults({ overall = {} } = {}) {
    return !!Object.keys(overall).length;
  }

  checkForTraceResults(results = {}) {
    return !!Object.keys(results || {}).length;
  }
}

export default AgentTargetTestResultsState;
